import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import { formatTimeWithTimezone } from "../../utils/displayHelpers";
import SidebarLayout from "./SidebarLayout";
// Walk-in (Phase 1): shared launcher, same component the dashboards use.
import WalkInButton from "../../components/WalkInButton";

// Test booking badge component
const TestBadge = ({ isTest }) => {
  if (!isTest) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        marginLeft: "8px",
        fontSize: "10px",
        fontWeight: "600",
        borderRadius: "4px",
        backgroundColor: "#f59e0b",
        color: "white",
        verticalAlign: "middle",
      }}
    >
      TEST
    </span>
  );
};

// Needs Assignment Badge component
const NeedsAssignmentBadge = ({ needsAssignment, importedStaffName }) => {
  if (!needsAssignment) return null;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        marginLeft: "8px",
        fontSize: "10px",
        fontWeight: "600",
        borderRadius: "4px",
        backgroundColor: "#ef4444",
        color: "white",
        verticalAlign: "middle",
      }}
      title={
        importedStaffName
          ? `Original CSV name: ${importedStaffName}`
          : "Needs staff assignment"
      }
    >
      ⚠️ NEEDS ASSIGNMENT
    </span>
  );
};

const Bookings = () => {
  const [searchParams] = useSearchParams();
  const preSelectedShopId = searchParams.get("shopId");

  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Tab state - Default to cancellations tab for shop owners
  const [activeTab, setActiveTab] = useState("bookings"); // 'bookings' or 'cancellations'

  // Filters
  // "Today" is the default (user, 2026-10-24): a desk opening this page wants the day it has to deliver, not the
  // whole history. "All" remains available, last in the row.
  const [statusFilter, setStatusFilter] = useState("today");
  const [selectedLocation, setSelectedLocation] = useState(
    preSelectedShopId || "all",
  );
  const [locations, setLocations] = useState([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [bookingsPerPage, setBookingsPerPage] = useState(25);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [refundType, setRefundType] = useState("full");
  const [partialRefundPercent, setPartialRefundPercent] = useState(50);
  const [useCredit, setUseCredit] = useState(false);
  const [processing, setProcessing] = useState(false);

  // A walk-in the desk asked to reopen from the list, to finish its payment. The modal
  // is opened onto it instead of the create form; cleared when the modal closes so the
  // "+ Walk-in" button still starts a new one afterwards.
  const [reopenWalkInId, setReopenWalkInId] = useState(null);

  // User info (for permissions). Seeded from the stored role so permission-gated controls (the location filter,
  // the Assign/Reassign buttons) do not flicker in a beat after the page paints.
  const [userRole, setUserRole] = useState(
    () => localStorage.getItem("ogestyle_role") || "",
  );

  // Staff reassignment state
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [reassignBooking, setReassignBooking] = useState(null);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchUserProfile();
    fetchLocations();
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, statusFilter, selectedLocation]);

  const fetchUserProfile = async () => {
    try {
      // Get role from localStorage (stored during login)
      const role = localStorage.getItem("ogestyle_role") || "";
      setUserRole(role);

      // Get staff record if professional
      if (role === "R3") {
        const token = localStorage.getItem("ogestyle_token");
        const apiBaseUrl =
          process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
        await axios.get(`${apiBaseUrl}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Staff ID is available via the profile API if needed
      }
    } catch (err) {
      // Don't fail the whole page if this fails
      const role = localStorage.getItem("ogestyle_role") || "";
      setUserRole(role);
    }
  };

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      const response = await axios.get(`${apiBaseUrl}/api/shop/my-shops`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // The endpoint answers `{ shops: [...] }`, NOT an array. Reading response.data
      // directly meant `locations` was ALWAYS empty, which silently disabled the
      // Location Filter below and made the walk-in button believe no location existed.
      const shops = response.data?.shops;
      setLocations(Array.isArray(shops) ? shops : []);
    } catch (err) {
      setLocations([]);
    }
  };

  const fetchBookings = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

      // Shop owners fetch all bookings, professionals fetch their own
      let endpoint = `${apiBaseUrl}/api/bookings/all`;
      if (userRole === "R3") {
        endpoint = `${apiBaseUrl}/api/bookings/staff`;
      }
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Log location distribution
      if (response.data.bookings) {
        const locationCounts = {};
        response.data.bookings.forEach((b) => {
          const locName = b.shopId?.name || "Unknown";
          locationCounts[locName] = (locationCounts[locName] || 0) + 1;
        });
      }

      setBookings(response.data.bookings || []);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...bookings];
    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "upcoming") {
        filtered = filtered.filter((b) => {
          const startDateTime = new Date(`${b.appointmentDate}T${b.startTime}`);
          return (
            ["pending", "confirmed"].includes(b.status) &&
            startDateTime > new Date()
          );
        });
      } else if (statusFilter === "today") {
        /**
         * TODAY (2026-10-24, user request: "can we have a Today tab and make it the default").
         *
         * Matching on the STORED `appointmentDate` string, not on a parsed Date: the field is the shop-local
         * calendar day ("YYYY-MM-DD"), so comparing it to a locally-formatted "today" keeps the tab on the day the
         * desk is standing in. Parsing it into a Date and comparing instants is what makes an evening appointment
         * fall off "today" in a browser that is in another timezone.
         *
         * Cancelled work is excluded — a desk opening "Today" wants the day it still has to deliver, and every
         * other status is kept so a completed morning is not hidden.
         */
        const today = dayjs().format("YYYY-MM-DD");
        filtered = filtered.filter((b) => {
          const bookingDate = String(b.appointmentDate || "").slice(0, 10);
          return bookingDate === today && b.status !== "cancelled";
        });
      } else {
        filtered = filtered.filter((b) => b.status === statusFilter);
      }
    }

    // Location filter
    if (selectedLocation !== "all") {
      filtered = filtered.filter((b) => b.shopId?._id === selectedLocation);
    }

    // Sort by date ascending (earliest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.appointmentDate);
      const dateB = new Date(b.appointmentDate);
      if (dateA.getTime() === dateB.getTime()) {
        return a.startTime.localeCompare(b.startTime);
      }
      return dateA - dateB; // Changed from dateB - dateA
    });
    setFilteredBookings(filtered);
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Permission checks
  const canConfirm = () => ["R1", "R2"].includes(userRole); // Shop owners only
  const canComplete = () => true; // All can mark complete
  const canCancel = () => ["R1", "R2"].includes(userRole); // Shop owners only (with refund)
  const canNoShow = () => true; // All can mark no-show

  const handleStatusUpdate = async (bookingId, newStatus) => {
    if (processing) return;

    // Validate permissions
    if (newStatus === "confirmed" && !canConfirm()) {
      alert("Only shop owners can confirm bookings");
      return;
    }
    if (newStatus === "cancelled" && !canCancel()) {
      alert("Only shop owners can cancel bookings. Please contact management.");
      return;
    }

    // Confirmation dialogs for Complete and No-Show
    if (newStatus === "completed") {
      const confirmed = window.confirm(
        "Mark this booking as completed? This action cannot be undone.",
      );
      if (!confirmed) return;
    }

    if (newStatus === "no-show") {
      const confirmed = window.confirm(
        "Mark this booking as no-show? This indicates the customer did not arrive for their appointment.",
      );
      if (!confirmed) return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

      await axios.patch(
        `${apiBaseUrl}/api/bookings/${bookingId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Refresh bookings
      await fetchBookings();
      alert(`Booking ${newStatus} successfully`);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update booking status");
    } finally {
      setProcessing(false);
    }
  };

  const openCancelModal = (booking) => {
    if (!canCancel()) {
      alert("Only shop owners can process cancellations and refunds");
      return;
    }
    setSelectedBooking(booking);
    setCancelReason("");

    // Calculate time-based refund suggestion (NOT enforced - owner can override)
    const appointmentDateTime = new Date(
      `${booking.appointmentDate}T${booking.startTime}`,
    );
    const now = new Date();
    const hoursUntil = (appointmentDateTime - now) / (1000 * 60 * 60);

    let suggestedPercent = 100;
    if (hoursUntil < 24) {
      suggestedPercent = 0;
    } else if (hoursUntil >= 24 && hoursUntil < 48) {
      suggestedPercent = 50;
    } else {
      suggestedPercent = 80;
    }

    // Set suggestion as default, but owner can change it
    if (suggestedPercent === 0) {
      setRefundType("none");
    } else if (suggestedPercent === 100) {
      setRefundType("full");
    } else {
      setRefundType("partial");
      setPartialRefundPercent(suggestedPercent);
    }

    setShowCancelModal(true);
  };

  const handleCancelWithRefund = async () => {
    if (!cancelReason) {
      alert("Please select a cancellation reason");
      return;
    }

    if (
      refundType === "partial" &&
      (partialRefundPercent < 0 || partialRefundPercent > 100)
    ) {
      alert("Partial refund must be between 0-100%");
      return;
    }

    try {
      setProcessing(true);
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

      /**
       * THE CAUSE, DERIVED FROM THE REASON ALREADY CHOSEN (2026-10-24). The shop's existing reason list maps
       * one-to-one onto the server's categories, so there is no second control to fill in. The category is what
       * decides the refund rule — `weather_emergency` and `professional_unavailable` are the business's own
       * responsibility, and `customer_request` is not.
       */
      const CATEGORY_BY_REASON = {
        customer_request: "customer_requested",
        professional_unavailable: "professional_unavailable",
        weather_emergency: "shop_force_majeure",
        no_show: "no_show",
        other: "other",
      };

      await axios.post(
        `${apiBaseUrl}/api/bookings/${selectedBooking._id}/cancel`,
        {
          reason: cancelReason,
          cancellationReasonCategory:
            CATEGORY_BY_REASON[cancelReason] || "other",
          refundType,
          refundPercent:
            refundType === "partial"
              ? partialRefundPercent
              : refundType === "full"
                ? 100
                : 0,
          useCredit,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setShowCancelModal(false);
      setSelectedBooking(null);
      await fetchBookings();
      alert("Booking cancelled and refund processed successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to cancel booking");
    } finally {
      setProcessing(false);
    }
  };

  // Staff reassignment handlers
  const openReassignModal = async (booking) => {
    if (!["R1", "R2"].includes(userRole)) {
      alert("Only shop owners/admins can reassign staff");
      return;
    }

    setReassignBooking(booking);
    setSelectedStaffId("");

    // Fetch available staff for this shop
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      // THE CANDIDATES COME FROM THE SERVER (2026-10-24). This used to list every active R3 at the shop, which
      // offered professionals who do not perform the service and who are already busy at that exact time.
      const response = await axios.get(
        `${apiBaseUrl}/api/bookings/${booking._id}/reassign-candidates`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Normalise the server's candidates into the shape the modal renders.
      const candidates = Array.isArray(response.data?.candidates)
        ? response.data.candidates
        : [];
      setAvailableStaff(
        candidates.map((candidate) => ({
          _id: candidate.id,
          name: candidate.name,
        })),
      );
      setShowReassignModal(true);
    } catch (err) {
      alert(
        err.response?.data?.message || "Failed to load eligible professionals",
      );
    }
  };

  const handleReassignStaff = async () => {
    if (!selectedStaffId) {
      alert("Please select a staff member");
      return;
    }

    try {
      setReassigning(true);
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

      await axios.patch(
        `${apiBaseUrl}/api/bookings/${reassignBooking._id}/reassign-staff`,
        { newStaffId: selectedStaffId },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setShowReassignModal(false);
      setReassignBooking(null);
      setSelectedStaffId("");
      await fetchBookings();
      alert("Staff reassigned successfully");
    } catch (err) {
      alert(err.response?.data?.message || "Failed to reassign staff");
    } finally {
      setReassigning(false);
    }
  };

  // Pagination
  const indexOfLastBooking = currentPage * bookingsPerPage;
  const indexOfFirstBooking = indexOfLastBooking - bookingsPerPage;
  const currentBookings = filteredBookings.slice(
    indexOfFirstBooking,
    indexOfLastBooking,
  );
  const totalPages = Math.ceil(filteredBookings.length / bookingsPerPage);

  /**
   * Which location a walk-in launched from this page belongs to.
   *
   * The real reason R1/R2 were told to "Select a location" was a shape mismatch in
   * `fetchLocations`: it read `response.data` as an array while `/api/shop/my-shops`
   * answers `{ shops: [...] }`, so `locations` was ALWAYS empty (which also silently
   * hid the Location Filter below). That is fixed; this resolution order is kept
   * because it is still the right precedence.
   *
   * Resolution order, most explicit first:
   *   1. the page's location filter (that IS the location picker for this page)
   *   2. the first location the API returned
   *   3. the location carried by the bookings on screen - they always have one, so this
   *      covers the case where the location list is empty for any reason
   *
   * An R1 who owns several locations can still change it inside the walk-in modal.
   */
  const walkInShopId = useMemo(() => {
    if (selectedLocation && selectedLocation !== "all") return selectedLocation;
    if (locations[0]?._id) return locations[0]._id;
    const withLocation = bookings.find((b) => b.shopId?._id || b.shopId);
    return (
      withLocation?.shopId?._id || withLocation?.shopId || null
    );
  }, [bookings, locations, selectedLocation]);

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "#FFA500";
      // A walk-in that was started but not yet paid for. Shown in amber, not the
      // default grey, because it is a held slot that will release itself.
      case "pending_payment":
        return "#F59E0B";
      case "reserved":
        return "#F59E0B";
      case "confirmed":
        return "#4CAF50";
      case "completed":
        return "#2196F3";
      case "cancelled":
        return "#F44336";
      case "no-show":
        return "#9E9E9E";
      default:
        return "#757575";
    }
  };

  const getStatusLabel = (status) => {
    // Raw statuses must never reach the screen: `pending_payment` is what the desk
    // sees for every walk-in that is still being paid for.
    const labels = {
      pending_payment: "Awaiting payment",
      reserved: "Reserved",
      "no-show": "No show",
    };
    if (labels[status]) return labels[status];
    return status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
  };

  /**
   * Release a walk-in that is still awaiting payment.
   *
   * Deliberately a different action from the normal Cancel: the booking has not been
   * paid for, so this releases the held slot rather than running the cancellation /
   * refund flow. The route is the same one the staff and freelancer pages use.
   */
  const cancelPendingWalkIn = async (booking) => {
    if (
      !window.confirm(
        "Cancel this walk-in? The held time slot will be released immediately.",
      )
    ) {
      return;
    }
    try {
      const token = localStorage.getItem("ogestyle_token");
      const apiBaseUrl =
        process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
      await axios.post(
        `${apiBaseUrl}/api/bookings/walk-in/${booking._id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await fetchBookings();
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to cancel this walk-in.");
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div
          className="bookings-page"
          style={{
            padding: "24px",
            textAlign: "center",
            fontFamily: "Inter, sans-serif",
          }}
        >
          <p style={{ color: "#666", fontSize: "14px" }}>Loading bookings...</p>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div
        className="bookings-page"
        style={{
          padding: "24px 12px",
          maxWidth: "100%",
          overflowX: "hidden",
          fontFamily: "Inter, sans-serif",
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: "24px",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "12px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "20px",
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: "22px",
                fontWeight: "600",
                color: "#1a1a1a",
              }}
            >
              Bookings
            </h1>
            <button
              onClick={() =>
                (window.location.href = "/dashboard/bookings/import")
              }
              style={{
                padding: "12px 20px",
                backgroundColor: "#0ea5e9",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              📥 Import Appointments
            </button>
            {/* Walk-in (Phase 1): R1/R2 must name the delivering professional. An R1
                who owns several locations also picks WHICH location inside the modal,
                so they are not stuck serving only the site their device is signed
                into; R2 is pinned to one location and gets no picker. */}
            <WalkInButton
              requireProvider
              shopId={walkInShopId}
              locations={locations}
              role={userRole}
              reopenBookingId={reopenWalkInId}
              onClose={() => setReopenWalkInId(null)}
            />
          </div>

          {/* Tab Navigation - Only show for shop owners */}
          {canCancel() && (
            <div
              style={{
                display: "flex",
                gap: "8px",
                marginBottom: "20px",
                borderBottom: "2px solid #e5e7eb",
              }}
            >
              <button
                onClick={() => setActiveTab("bookings")}
                style={{
                  padding: "12px 24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: activeTab === "bookings" ? "#7C3AED" : "#666",
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom:
                    activeTab === "bookings"
                      ? "2px solid #7C3AED"
                      : "2px solid transparent",
                  cursor: "pointer",
                  marginBottom: "-2px",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                All Bookings
              </button>
              <button
                onClick={() => setActiveTab("cancellations")}
                style={{
                  padding: "12px 24px",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: activeTab === "cancellations" ? "#7C3AED" : "#666",
                  backgroundColor: "transparent",
                  border: "none",
                  borderBottom:
                    activeTab === "cancellations"
                      ? "2px solid #7C3AED"
                      : "2px solid transparent",
                  cursor: "pointer",
                  marginBottom: "-2px",
                  fontFamily: "Inter, sans-serif",
                  position: "relative",
                }}
              >
                Cancellation Requests
                {bookings.filter(
                  (b) => b.cancellationRequest?.status === "pending",
                ).length > 0 && (
                  <span
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      backgroundColor: "#ef4444",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "700",
                      padding: "2px 6px",
                      borderRadius: "10px",
                      minWidth: "18px",
                      textAlign: "center",
                    }}
                  >
                    {
                      bookings.filter(
                        (b) => b.cancellationRequest?.status === "pending",
                      ).length
                    }
                  </span>
                )}
              </button>
            </div>
          )}

          {activeTab === "bookings" && (
            <>
              {/* Imported rows whose professional is not on the team are waiting for the shop to assign
                  them. They can be on any date, so the "Today" default hides them — say so instead of
                  leaving the owner thinking the import did nothing. */}
              {bookings.some((b) => b.needsStaffAssignment) && (
                <div
                  style={{
                    display: "flex",
                    gap: "12px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: "10px",
                    padding: "10px 14px",
                    marginBottom: "12px",
                    fontSize: "14px",
                  }}
                >
                  <strong>
                    {bookings.filter((b) => b.needsStaffAssignment).length} imported appointment(s)
                    need a professional assigned
                  </strong>
                  <span style={{ color: "#92400e" }}>
                    They may be outside the dates shown below.
                  </span>
                  <button
                    type="button"
                    onClick={() => setStatusFilter("all")}
                    style={{
                      border: "1px solid #d97706",
                      background: "#fff",
                      color: "#92400e",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Show them
                  </button>
                </div>
              )}

              {/* Filters */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                {/* Status Filter */}
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {[
                    "today",
                    "upcoming",
                    "pending",
                    "confirmed",
                    "completed",
                    "cancelled",
                    "no-show",
                    "all",
                  ].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      style={{
                        padding: "8px 16px",
                        fontSize: "13px",
                        fontWeight: statusFilter === status ? "600" : "400",
                        color: statusFilter === status ? "white" : "#666",
                        backgroundColor:
                          statusFilter === status ? "#7C3AED" : "#f5f5f5",
                        border: "none",
                        borderRadius: "20px",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        fontFamily: "Inter, sans-serif",
                      }}
                    >
                      {status === "all"
                        ? "All"
                        : status === "upcoming"
                          ? "Upcoming"
                          : status === "today"
                            ? "Today"
                            : getStatusLabel(status)}
                    </button>
                  ))}
                </div>

                {/**
                 * LOCATION FILTER — R1 ONLY, AND LABELLED BY PLACE (2026-10-24).
                 *
                 * An R2 is assigned to a single location and cannot see any other, so a filter offering them is a
                 * control that can only ever say "All Locations" and mean nothing (user: *"R2 cannot see other
                 * locations, so why is R2 profile having a dropdown"*).
                 *
                 * And the value is CITY + STATE, not the business name: two branches of one business share a name,
                 * so a list of names cannot tell the desk which place it is filtering to.
                 */}
                {locations.length > 1 && ["R1"].includes(userRole) && (
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    style={{
                      padding: "10px",
                      fontSize: "14px",
                      border: "1px solid #ddd",
                      borderRadius: "8px",
                      backgroundColor: "white",
                      fontFamily: "Inter, sans-serif",
                      cursor: "pointer",
                    }}
                  >
                    <option value="all">All Locations</option>
                    {locations.map((loc) => (
                      <option key={loc._id} value={loc._id}>
                        {loc.city && loc.state
                          ? `${loc.city}, ${String(loc.state).toUpperCase()}`
                          : loc.city || loc.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Results Count */}
              <p
                style={{
                  marginTop: "16px",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                Showing {currentBookings.length} of {filteredBookings.length}{" "}
                bookings
              </p>

              {/* Per Page Selector */}
              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                }}
              >
                <label style={{ color: "#666" }}>Show:</label>
                <select
                  value={bookingsPerPage}
                  onChange={(e) => {
                    setBookingsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 10px",
                    fontSize: "13px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    backgroundColor: "white",
                    fontFamily: "Inter, sans-serif",
                    cursor: "pointer",
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                  <option value={500}>500</option>
                </select>
                <span style={{ color: "#666" }}>per page</span>
              </div>
            </>
          )}
        </div>

        {/* Pending Cancellation Requests - Always visible for shop owners */}
        {canCancel() &&
          bookings.filter((b) => b.cancellationRequest?.status === "pending")
            .length > 0 &&
          activeTab === "bookings" && (
            <div style={{ marginBottom: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2
                  style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1a1a1a",
                    margin: 0,
                  }}
                >
                  ⚠️ Pending Cancellation Requests
                </h2>
                <span
                  style={{
                    backgroundColor: "#ef4444",
                    color: "white",
                    fontSize: "12px",
                    fontWeight: "700",
                    padding: "4px 12px",
                    borderRadius: "12px",
                  }}
                >
                  {
                    bookings.filter(
                      (b) => b.cancellationRequest?.status === "pending",
                    ).length
                  }{" "}
                  Pending
                </span>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    window.innerWidth <= 768
                      ? "1fr"
                      : "repeat(auto-fill, minmax(320px, 1fr))",
                  gap: "16px",
                }}
              >
                {bookings
                  .filter((b) => b.cancellationRequest?.status === "pending")
                  .map((booking) => (
                    <div
                      key={booking._id}
                      style={{
                        backgroundColor: "#fef2f2",
                        border: "2px solid #fecaca",
                        padding: "16px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 4px rgba(239,68,68,0.1)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <h3
                            style={{
                              margin: "0 0 8px 0",
                              fontSize: "16px",
                              fontWeight: "600",
                              color: "#1a1a1a",
                            }}
                          >
                            {booking.serviceId?.name || "N/A"}
                            <TestBadge isTest={booking.isTestBooking} />
                          </h3>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#666",
                              lineHeight: "1.6",
                            }}
                          >
                            <p style={{ margin: "4px 0" }}>
                              <strong>Customer:</strong>{" "}
                              {booking.customerId
                                ? `${booking.customerId.firstName} ${booking.customerId.lastName}`
                                : booking.customerName || "Walk-in"}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Date:</strong>{" "}
                              {dayjs(booking.appointmentDate).format(
                                "MMM D, YYYY",
                              )}{" "}
                              at{" "}
                              {formatTimeWithTimezone(
                                booking.startTime,
                                dayjs(booking.appointmentDate).format(
                                  "YYYY-MM-DD",
                                ),
                                booking.shopId?.timezone || "America/Chicago",
                              )}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Professional:</strong>{" "}
                              {booking.staffId ? (
                                `${booking.staffId.userId?.firstName || ""} ${booking.staffId.userId?.lastName || ""}`.trim() ||
                                "Unknown"
                              ) : (
                                <span style={{ color: "#ef4444" }}>
                                  Unassigned
                                </span>
                              )}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Location:</strong>{" "}
                              {booking.shopId?.address?.city &&
                              booking.shopId?.address?.state
                                ? `${booking.shopId.address.city}, ${booking.shopId.address.state}`
                                : booking.shopId?.name || "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          backgroundColor: "white",
                          padding: "12px",
                          borderRadius: "8px",
                          marginBottom: "12px",
                          borderLeft: "3px solid #ef4444",
                        }}
                      >
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "12px",
                            fontWeight: "600",
                            color: "#ef4444",
                          }}
                        >
                          Cancellation Reason:
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "14px",
                            color: "#1a1a1a",
                          }}
                        >
                          {booking.cancellationRequest?.reason ||
                            "No reason provided"}
                        </p>
                        <p
                          style={{
                            margin: "8px 0 0 0",
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          Requested:{" "}
                          {dayjs(
                            booking.cancellationRequest?.requestedAt,
                          ).format("MMM D, YYYY h:mm A")}
                        </p>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowCancelModal("approve");
                          }}
                          disabled={processing}
                          style={{
                            flex: 1,
                            minWidth: "120px",
                            padding: "10px 16px",
                            fontSize: "14px",
                            fontWeight: "500",
                            backgroundColor: processing ? "#ccc" : "#10b981",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: processing ? "not-allowed" : "pointer",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          ✓ Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedBooking(booking);
                            setShowCancelModal("deny");
                          }}
                          disabled={processing}
                          style={{
                            flex: 1,
                            minWidth: "120px",
                            padding: "10px 16px",
                            fontSize: "14px",
                            fontWeight: "500",
                            backgroundColor: processing ? "#ccc" : "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: processing ? "not-allowed" : "pointer",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          ✗ Deny
                        </button>
                      </div>
                    </div>
                  ))}
              </div>

              {bookings.filter(
                (b) => b.cancellationRequest?.status === "pending",
              ).length > 3 && (
                <button
                  onClick={() => setActiveTab("cancellations")}
                  style={{
                    width: "100%",
                    marginTop: "12px",
                    padding: "10px",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor: "white",
                    color: "#7C3AED",
                    border: "2px solid #7C3AED",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  View All{" "}
                  {
                    bookings.filter(
                      (b) => b.cancellationRequest?.status === "pending",
                    ).length
                  }{" "}
                  Requests →
                </button>
              )}
            </div>
          )}

        {/* Error Message */}
        {activeTab === "bookings" && error && (
          <div
            style={{
              padding: "16px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "8px",
              marginBottom: "16px",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        )}

        {/* Bookings Table/Cards - Only show on All Bookings tab */}
        {activeTab === "bookings" &&
          (currentBookings.length === 0 ? (
            <div
              style={{
                backgroundColor: "white",
                padding: "40px 20px",
                borderRadius: "12px",
                textAlign: "center",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <p style={{ fontSize: "16px", color: "#666", margin: 0 }}>
                {statusFilter === "today"
                  ? "No bookings for today"
                  : "No bookings found"}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div
                style={{
                  display: isMobile ? "none" : "block",
                  backgroundColor: "white",
                  borderRadius: "12px",
                  overflow: "hidden",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "14px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor: "#f9fafb",
                        borderBottom: "1px solid #e5e7eb",
                      }}
                    >
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Date
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Time
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Service
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Customer
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Professional
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Location
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "left",
                          fontWeight: "600",
                          color: "#374151",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentBookings.map((booking) => (
                      <tr
                        key={booking._id}
                        style={{
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {dayjs(booking.appointmentDate).format("M/D/YY")}
                        </td>
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {formatTimeWithTimezone(
                            booking.startTime,
                            dayjs(booking.appointmentDate).format("YYYY-MM-DD"),
                            booking.shopId?.timezone || "America/Chicago",
                          )}
                        </td>
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {booking.serviceId?.name || "N/A"}
                          <TestBadge isTest={booking.isTestBooking} />
                        </td>
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {booking.customerId
                            ? `${booking.customerId.firstName} ${booking.customerId.lastName}`
                            : booking.customerName || "Walk-in"}
                        </td>
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {booking.staffId ? (
                            `${booking.staffId.userId?.firstName || ""} ${booking.staffId.userId?.lastName || ""}`.trim() ||
                            "Unknown"
                          ) : (
                            <span
                              style={{ color: "#ef4444", fontWeight: "500" }}
                            >
                              Unassigned
                            </span>
                          )}
                          <NeedsAssignmentBadge
                            needsAssignment={booking.needsStaffAssignment}
                            importedStaffName={booking.importedStaffName}
                          />
                          {booking.needsStaffAssignment &&
                            ["R1", "R2"].includes(userRole) && (
                              <button
                                onClick={() => openReassignModal(booking)}
                                title="This imported appointment has no professional yet — choose one"
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 8px",
                                  fontSize: "10px",
                                  backgroundColor: "#0ea5e9",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  verticalAlign: "middle",
                                }}
                              >
                                Assign
                              </button>
                            )}
                          {/* REAL REASSIGNMENT (2026-10-24). The Assign button above is only for an imported
                              booking with no professional; this one moves an already-assigned appointment, which
                              is the case the shop actually reports ("the professional is sick"). */}
                          {!booking.needsStaffAssignment &&
                            ["R1", "R2"].includes(userRole) &&
                            booking.staffId &&
                            ["pending", "confirmed", "pending_payment", "slot_conflict"].includes(
                              booking.status,
                            ) && (
                              <button
                                onClick={() => openReassignModal(booking)}
                                title="Move this appointment to another professional (e.g. someone is off sick)"
                                style={{
                                  marginLeft: "8px",
                                  padding: "2px 8px",
                                  fontSize: "10px",
                                  backgroundColor: "#6366f1",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: "pointer",
                                  verticalAlign: "middle",
                                }}
                              >
                                Reassign
                              </button>
                            )}
                        </td>
                        <td style={{ padding: "12px", color: "#1f2937" }}>
                          {booking.shopId?.address?.city &&
                          booking.shopId?.address?.state
                            ? `${booking.shopId.address.city}, ${booking.shopId.address.state}`
                            : booking.shopId?.name || "N/A"}
                        </td>
                        <td style={{ padding: "12px" }}>
                          <span
                            style={{
                              padding: "4px 12px",
                              borderRadius: "12px",
                              fontSize: "12px",
                              fontWeight: "500",
                              backgroundColor: `${getStatusColor(booking.status)}20`,
                              color: getStatusColor(booking.status),
                            }}
                          >
                            {getStatusLabel(booking.status)}
                          </span>
                        </td>
                        <td style={{ padding: "12px" }}>
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            {/* Walk-in awaiting payment: the ONLY action that applies.
                                It releases the held slot instead of running the normal
                                cancellation flow, because nothing has been paid. */}
                            {booking.isWalkIn &&
                              booking.status === "pending_payment" && (
                                <>
                                  {/* Being able to get back IN matters as much as being
                                      able to cancel. Sending the link is not the same
                                      as being paid - phones die, cards get declined -
                                      so the desk can reopen it to show the QR again or
                                      re-send the link, rather than releasing a slot the
                                      customer is still holding. */}
                                  <button
                                    onClick={() => setReopenWalkInId(booking._id)}
                                    disabled={processing}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: "#eff6ff",
                                      color: "#1e40af",
                                      border: "1px solid #bfdbfe",
                                      borderRadius: "6px",
                                      cursor: processing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: "600",
                                      fontFamily: "Inter, sans-serif",
                                    }}
                                  >
                                    Finish payment
                                  </button>
                                  <button
                                    onClick={() => cancelPendingWalkIn(booking)}
                                    disabled={processing}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: "#fee2e2",
                                      color: "#991b1b",
                                      border: "1px solid #fecaca",
                                      borderRadius: "6px",
                                      cursor: processing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontWeight: "600",
                                      fontFamily: "Inter, sans-serif",
                                    }}
                                  >
                                    Cancel walk-in
                                  </button>
                                </>
                              )}
                            {booking.status === "pending" && canConfirm() && (
                              <button
                                onClick={() =>
                                  handleStatusUpdate(booking._id, "confirmed")
                                }
                                disabled={processing}
                                style={{
                                  padding: "6px 12px",
                                  fontSize: "12px",
                                  backgroundColor: "#4CAF50",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: processing
                                    ? "not-allowed"
                                    : "pointer",
                                  fontFamily: "Inter, sans-serif",
                                }}
                              >
                                Confirm
                              </button>
                            )}
                            {["pending", "confirmed"].includes(
                              booking.status,
                            ) &&
                              canComplete() && (
                                <button
                                  onClick={() =>
                                    handleStatusUpdate(booking._id, "completed")
                                  }
                                  disabled={processing}
                                  style={{
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    backgroundColor: "#2196F3",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "6px",
                                    cursor: processing
                                      ? "not-allowed"
                                      : "pointer",
                                    fontFamily: "Inter, sans-serif",
                                  }}
                                >
                                  Complete
                                </button>
                              )}
                            {["pending", "confirmed"].includes(
                              booking.status,
                            ) && (
                              <>
                                {canCancel() ? (
                                  <button
                                    onClick={() => openCancelModal(booking)}
                                    disabled={processing}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: "#F44336",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: processing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontFamily: "Inter, sans-serif",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                ) : null}
                                {canNoShow() && (
                                  <button
                                    onClick={() =>
                                      handleStatusUpdate(booking._id, "no-show")
                                    }
                                    disabled={processing}
                                    style={{
                                      padding: "6px 12px",
                                      fontSize: "12px",
                                      backgroundColor: "#9E9E9E",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: processing
                                        ? "not-allowed"
                                        : "pointer",
                                      fontFamily: "Inter, sans-serif",
                                    }}
                                  >
                                    No-Show
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div
                style={{
                  display: isMobile ? "block" : "none",
                }}
              >
                {currentBookings.map((booking) => (
                  <div
                    key={booking._id}
                    style={{
                      backgroundColor: "white",
                      padding: "16px",
                      borderRadius: "12px",
                      marginBottom: "12px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "12px",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: "0 0 4px 0",
                            fontSize: "16px",
                            fontWeight: "600",
                            color: "#1a1a1a",
                          }}
                        >
                          {booking.serviceId?.name || "N/A"}
                          <TestBadge isTest={booking.isTestBooking} />
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "13px",
                            color: "#666",
                          }}
                        >
                          {dayjs(booking.appointmentDate).format("M/D/YY")} at{" "}
                          {formatTimeWithTimezone(
                            booking.startTime,
                            dayjs(booking.appointmentDate).format("YYYY-MM-DD"),
                            booking.shopId?.timezone || "America/Chicago",
                          )}
                        </p>
                      </div>
                      <span
                        style={{
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "500",
                          backgroundColor: `${getStatusColor(booking.status)}20`,
                          color: getStatusColor(booking.status),
                          height: "fit-content",
                        }}
                      >
                        {getStatusLabel(booking.status)}
                      </span>
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                        marginBottom: "12px",
                        lineHeight: "1.6",
                      }}
                    >
                      <p style={{ margin: "4px 0" }}>
                        <strong>Customer:</strong>{" "}
                        {booking.customerId
                          ? `${booking.customerId.firstName} ${booking.customerId.lastName}`
                          : booking.customerName || "Walk-in"}
                      </p>
                      <p style={{ margin: "4px 0" }}>
                        <strong>Professional:</strong>{" "}
                        {booking.staffId ? (
                          `${booking.staffId.userId?.firstName || ""} ${booking.staffId.userId?.lastName || ""}`.trim() ||
                          "Unknown"
                        ) : (
                          <span style={{ color: "#ef4444", fontWeight: "500" }}>
                            Unassigned
                          </span>
                        )}
                        <NeedsAssignmentBadge
                          needsAssignment={booking.needsStaffAssignment}
                          importedStaffName={booking.importedStaffName}
                        />
                      </p>
                      <p style={{ margin: "4px 0" }}>
                        <strong>Location:</strong>{" "}
                        {booking.shopId?.address?.city &&
                        booking.shopId?.address?.state
                          ? `${booking.shopId.address.city}, ${booking.shopId.address.state}`
                          : booking.shopId?.name || "N/A"}
                      </p>
                    </div>

                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      {booking.needsStaffAssignment &&
                        ["R1", "R2"].includes(userRole) && (
                          <button
                            onClick={() => openReassignModal(booking)}
                            style={{
                              flex: 1,
                              minWidth: "80px",
                              padding: "8px 12px",
                              fontSize: "13px",
                              backgroundColor: "#0ea5e9",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Assign Staff
                          </button>
                        )}
                      {/* REAL REASSIGNMENT (2026-10-24): moves an already-assigned appointment. */}
                      {!booking.needsStaffAssignment &&
                        ["R1", "R2"].includes(userRole) &&
                        booking.staffId &&
                        ["pending", "confirmed", "pending_payment", "slot_conflict"].includes(
                          booking.status,
                        ) && (
                          <button
                            onClick={() => openReassignModal(booking)}
                            style={{
                              flex: 1,
                              minWidth: "80px",
                              padding: "8px 12px",
                              fontSize: "13px",
                              backgroundColor: "#6366f1",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Reassign
                          </button>
                        )}
                      {/* Walk-in awaiting payment: reopen it to finish the payment, or
                          release the held slot. Nothing has been paid, so the normal
                          cancellation flow does not apply. */}
                      {booking.isWalkIn &&
                        booking.status === "pending_payment" && (
                          <button
                            onClick={() => setReopenWalkInId(booking._id)}
                            disabled={processing}
                            style={{
                              flex: 1,
                              minWidth: "80px",
                              padding: "8px 12px",
                              fontSize: "13px",
                              backgroundColor: "#eff6ff",
                              color: "#1e40af",
                              border: "1px solid #bfdbfe",
                              borderRadius: "6px",
                              cursor: processing ? "not-allowed" : "pointer",
                              fontWeight: "600",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Finish payment
                          </button>
                        )}
                      {booking.isWalkIn &&
                        booking.status === "pending_payment" && (
                          <button
                            onClick={() => cancelPendingWalkIn(booking)}
                            disabled={processing}
                            style={{
                              flex: 1,
                              minWidth: "80px",
                              padding: "8px 12px",
                              fontSize: "13px",
                              backgroundColor: "#fee2e2",
                              color: "#991b1b",
                              border: "1px solid #fecaca",
                              borderRadius: "6px",
                              cursor: processing ? "not-allowed" : "pointer",
                              fontWeight: "600",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Cancel walk-in
                          </button>
                        )}
                      {booking.status === "pending" && canConfirm() && (
                        <button
                          onClick={() =>
                            handleStatusUpdate(booking._id, "confirmed")
                          }
                          disabled={processing}
                          style={{
                            flex: 1,
                            minWidth: "80px",
                            padding: "8px 12px",
                            fontSize: "13px",
                            backgroundColor: "#4CAF50",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: processing ? "not-allowed" : "pointer",
                            fontFamily: "Inter, sans-serif",
                          }}
                        >
                          Confirm
                        </button>
                      )}
                      {["pending", "confirmed"].includes(booking.status) &&
                        canComplete() && (
                          <button
                            onClick={() =>
                              handleStatusUpdate(booking._id, "completed")
                            }
                            disabled={processing}
                            style={{
                              flex: 1,
                              minWidth: "80px",
                              padding: "8px 12px",
                              fontSize: "13px",
                              backgroundColor: "#2196F3",
                              color: "white",
                              border: "none",
                              borderRadius: "6px",
                              cursor: processing ? "not-allowed" : "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            Complete
                          </button>
                        )}
                      {["pending", "confirmed"].includes(booking.status) && (
                        <>
                          {canCancel() ? (
                            <button
                              onClick={() => openCancelModal(booking)}
                              disabled={processing}
                              style={{
                                flex: 1,
                                minWidth: "80px",
                                padding: "8px 12px",
                                fontSize: "13px",
                                backgroundColor: "#F44336",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: processing ? "not-allowed" : "pointer",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              Cancel
                            </button>
                          ) : null}
                          {canNoShow() && (
                            <button
                              onClick={() =>
                                handleStatusUpdate(booking._id, "no-show")
                              }
                              disabled={processing}
                              style={{
                                flex: 1,
                                minWidth: "80px",
                                padding: "8px 12px",
                                fontSize: "13px",
                                backgroundColor: "#9E9E9E",
                                color: "white",
                                border: "none",
                                borderRadius: "6px",
                                cursor: processing ? "not-allowed" : "pointer",
                                fontFamily: "Inter, sans-serif",
                              }}
                            >
                              No-Show
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                    marginTop: "24px",
                    padding: "20px",
                    backgroundColor: "white",
                    borderRadius: "12px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      backgroundColor:
                        currentPage === 1 ? "#f5f5f5" : "#7C3AED",
                      color: currentPage === 1 ? "#999" : "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "8px 16px",
                      fontSize: "14px",
                      backgroundColor:
                        currentPage === totalPages ? "#f5f5f5" : "#7C3AED",
                      color: currentPage === totalPages ? "#999" : "white",
                      border: "none",
                      borderRadius: "8px",
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          ))}

        {/* Cancellation Requests Tab */}
        {activeTab === "cancellations" && (
          <div>
            <h2
              style={{
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "16px",
                color: "#1a1a1a",
              }}
            >
              Pending Cancellation Requests
            </h2>

            {bookings.filter((b) => b.cancellationRequest?.status === "pending")
              .length === 0 ? (
              <div
                style={{
                  backgroundColor: "white",
                  padding: "40px 20px",
                  borderRadius: "12px",
                  textAlign: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <p style={{ fontSize: "16px", color: "#666", margin: 0 }}>
                  No pending cancellation requests
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {bookings
                  .filter((b) => b.cancellationRequest?.status === "pending")
                  .map((booking) => (
                    <div
                      key={booking._id}
                      style={{
                        backgroundColor: "white",
                        padding: "20px",
                        borderRadius: "12px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        borderLeft: "4px solid #f59e0b",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          marginBottom: "16px",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <div style={{ flex: 1, minWidth: "250px" }}>
                          <h3
                            style={{
                              fontSize: "16px",
                              fontWeight: "600",
                              marginBottom: "8px",
                              color: "#1a1a1a",
                            }}
                          >
                            {booking.serviceId?.name || "Unknown Service"}
                            <TestBadge isTest={booking.isTestBooking} />
                          </h3>
                          <div
                            style={{
                              fontSize: "14px",
                              color: "#666",
                              lineHeight: "1.6",
                            }}
                          >
                            <p style={{ margin: "4px 0" }}>
                              <strong>Customer:</strong>{" "}
                              {booking.customerId?.firstName}{" "}
                              {booking.customerId?.lastName}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Date:</strong>{" "}
                              {dayjs(booking.appointmentDate).format(
                                "MMM DD, YYYY",
                              )}{" "}
                              at{" "}
                              {formatTimeWithTimezone(
                                booking.startTime,
                                dayjs(booking.appointmentDate).format(
                                  "YYYY-MM-DD",
                                ),
                                booking.shopId?.timezone || "America/Chicago",
                              )}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Location:</strong>{" "}
                              {booking.shopId?.name || "N/A"}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Staff:</strong>{" "}
                              {booking.staffId?.userId?.firstName}{" "}
                              {booking.staffId?.userId?.lastName}
                            </p>
                            <p style={{ margin: "4px 0" }}>
                              <strong>Requested:</strong>{" "}
                              {dayjs(
                                booking.cancellationRequest.requestedAt,
                              ).format("MMM DD, YYYY h:mm A")}
                            </p>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                          }}
                        >
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowCancelModal("approve");
                            }}
                            style={{
                              padding: "10px 20px",
                              fontSize: "14px",
                              fontWeight: "600",
                              backgroundColor: "#10b981",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            ✓ Approve
                          </button>
                          <button
                            onClick={() => {
                              setSelectedBooking(booking);
                              setShowCancelModal("deny");
                            }}
                            style={{
                              padding: "10px 20px",
                              fontSize: "14px",
                              fontWeight: "600",
                              backgroundColor: "#ef4444",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              cursor: "pointer",
                              fontFamily: "Inter, sans-serif",
                            }}
                          >
                            ✗ Deny
                          </button>
                        </div>
                      </div>
                      <div
                        style={{
                          backgroundColor: "#fef3c7",
                          padding: "12px",
                          borderRadius: "8px",
                          marginTop: "12px",
                        }}
                      >
                        <p
                          style={{
                            fontSize: "13px",
                            color: "#92400e",
                            margin: 0,
                          }}
                        >
                          <strong>Reason:</strong>{" "}
                          {booking.cancellationRequest.reason}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {/* Approval/Denial Modal */}
        {showCancelModal && showCancelModal !== true && selectedBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "30px",
                borderRadius: "16px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
              }}
            >
              <h2
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  marginBottom: "20px",
                  color: showCancelModal === "approve" ? "#10b981" : "#ef4444",
                }}
              >
                {showCancelModal === "approve"
                  ? "Approve Cancellation Request"
                  : "Deny Cancellation Request"}
              </h2>

              <div
                style={{
                  marginBottom: "20px",
                  fontSize: "14px",
                  color: "#666",
                }}
              >
                <p>
                  <strong>Service:</strong> {selectedBooking.serviceId?.name}
                </p>
                <p>
                  <strong>Customer:</strong>{" "}
                  {selectedBooking.customerId?.firstName}{" "}
                  {selectedBooking.customerId?.lastName}
                </p>
                <p>
                  <strong>Date:</strong>{" "}
                  {dayjs(selectedBooking.appointmentDate).format(
                    "MMM DD, YYYY",
                  )}{" "}
                  at{" "}
                  {formatTimeWithTimezone(
                    selectedBooking.startTime,
                    dayjs(selectedBooking.appointmentDate).format("YYYY-MM-DD"),
                    selectedBooking.shopId?.timezone || "America/Chicago",
                  )}
                </p>
                <p>
                  <strong>Staff Reason:</strong>{" "}
                  {selectedBooking.cancellationRequest?.reason}
                </p>
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "14px",
                    fontWeight: "600",
                    marginBottom: "8px",
                    color: "#374151",
                  }}
                >
                  {showCancelModal === "approve"
                    ? "Approval Notes (optional)"
                    : "Reason for Denial"}
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder={
                    showCancelModal === "approve"
                      ? "Add any notes..."
                      : "Why is this request being denied?"
                  }
                  style={{
                    width: "100%",
                    padding: "12px",
                    fontSize: "14px",
                    border: "2px solid #e5e7eb",
                    borderRadius: "8px",
                    fontFamily: "Inter, sans-serif",
                    minHeight: "100px",
                    resize: "vertical",
                  }}
                />
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedBooking(null);
                    setCancelReason("");
                  }}
                  disabled={processing}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    backgroundColor: "#f3f4f6",
                    color: "#374151",
                    border: "none",
                    borderRadius: "8px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (showCancelModal === "deny" && !cancelReason) {
                      alert("Please provide a reason for denying this request");
                      return;
                    }

                    try {
                      setProcessing(true);
                      const token = localStorage.getItem("ogestyle_token");
                      const apiBaseUrl =
                        process.env.REACT_APP_API_BASE_URL ||
                        "https://api.ogestyle.com";

                      await axios.put(
                        `${apiBaseUrl}/api/bookings/${selectedBooking._id}/cancellation-request/respond`,
                        {
                          approved: showCancelModal === "approve",
                          reviewNotes: cancelReason,
                        },
                        { headers: { Authorization: `Bearer ${token}` } },
                      );

                      setShowCancelModal(false);
                      setSelectedBooking(null);
                      setCancelReason("");
                      await fetchBookings();

                      alert(
                        showCancelModal === "approve"
                          ? "Cancellation request approved successfully"
                          : "Cancellation request denied",
                      );
                    } catch (err) {
                      alert(
                        err.response?.data?.message ||
                          "Failed to process request",
                      );
                    } finally {
                      setProcessing(false);
                    }
                  }}
                  disabled={processing}
                  style={{
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: "600",
                    backgroundColor:
                      showCancelModal === "approve" ? "#10b981" : "#ef4444",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                    opacity: processing ? 0.6 : 1,
                  }}
                >
                  {processing
                    ? "Processing..."
                    : showCancelModal === "approve"
                      ? "Approve"
                      : "Deny"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cancellation Modal */}
        {showCancelModal && selectedBooking && showCancelModal === true && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                borderRadius: "12px",
                padding: "24px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflowY: "auto",
              }}
            >
              <h2
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "20px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                }}
              >
                Cancel Booking
              </h2>

              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                <p style={{ margin: "0 0 4px 0" }}>
                  <strong>Service:</strong> {selectedBooking.serviceId?.name}
                </p>
                <p style={{ margin: "0 0 4px 0" }}>
                  <strong>Date:</strong>{" "}
                  {dayjs(selectedBooking.appointmentDate).format("MMM D, YYYY")}{" "}
                  at{" "}
                  {formatTimeWithTimezone(
                    selectedBooking.startTime,
                    dayjs(selectedBooking.appointmentDate).format("YYYY-MM-DD"),
                    selectedBooking.shopId?.timezone || "America/Chicago",
                  )}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Amount:</strong> $
                  {selectedBooking.servicePriceAtBooking}
                </p>
              </div>

              {/* Time-Based Refund Suggestion */}
              {(() => {
                const appointmentDateTime = new Date(
                  `${selectedBooking.appointmentDate}T${selectedBooking.startTime}`,
                );
                const now = new Date();
                const hoursUntil = Math.max(
                  0,
                  (appointmentDateTime - now) / (1000 * 60 * 60),
                );

                let suggestedPercent = 100;
                let colorScheme = "#10b981"; // green
                if (hoursUntil < 24) {
                  suggestedPercent = 0;
                  colorScheme = "#ef4444"; // red
                } else if (hoursUntil >= 24 && hoursUntil < 48) {
                  suggestedPercent = 50;
                  colorScheme = "#f59e0b"; // orange
                } else {
                  suggestedPercent = 80;
                  colorScheme = "#10b981"; // green
                }

                return (
                  <div
                    style={{
                      padding: "16px",
                      backgroundColor: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "8px",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#1e40af",
                        marginBottom: "12px",
                      }}
                    >
                      💡 Suggested Refund (You can override below)
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#374151" }}>
                        Time until appointment:
                      </span>
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#1e40af",
                        }}
                      >
                        {hoursUntil.toFixed(1)} hours
                      </span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#374151" }}>
                        Suggested refund:
                      </span>
                      <span
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: colorScheme,
                        }}
                      >
                        {suggestedPercent}% ($
                        {(
                          (selectedBooking.servicePriceAtBooking *
                            suggestedPercent) /
                          100
                        ).toFixed(2)}
                        )
                      </span>
                    </div>
                    <div
                      style={{
                        marginTop: "12px",
                        padding: "8px",
                        backgroundColor: "white",
                        borderRadius: "6px",
                        fontSize: "12px",
                        color: "#6b7280",
                      }}
                    >
                      <strong>Policy:</strong>{" "}
                      {hoursUntil < 24
                        ? "Less than 24 hours notice"
                        : hoursUntil < 48
                          ? "24-48 hours notice"
                          : "48+ hours notice"}
                    </div>
                  </div>
                );
              })()}

              {/* Cancellation Reason */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Cancellation Reason *
                </label>
                <select
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px",
                    fontSize: "14px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    backgroundColor: "white",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  <option value="">Select a reason...</option>
                  <option value="customer_request">Customer Request</option>
                  <option value="professional_unavailable">
                    Professional Unavailable
                  </option>
                  <option value="weather_emergency">Weather/Emergency</option>
                  <option value="no_show">Customer No-Show</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Refund Type */}
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Refund Option{" "}
                  <span style={{ color: "#6b7280", fontWeight: "normal" }}>
                    (Override suggestion if needed)
                  </span>
                </label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      value="full"
                      checked={refundType === "full"}
                      onChange={(e) => setRefundType(e.target.value)}
                      style={{ marginRight: "8px" }}
                    />
                    Full Refund (100%)
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      value="partial"
                      checked={refundType === "partial"}
                      onChange={(e) => setRefundType(e.target.value)}
                      style={{ marginRight: "8px" }}
                    />
                    Partial Refund
                  </label>
                  {refundType === "partial" && (
                    <div style={{ marginLeft: "28px", marginTop: "8px" }}>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={partialRefundPercent}
                        onChange={(e) =>
                          setPartialRefundPercent(Number(e.target.value))
                        }
                        style={{
                          width: "80px",
                          padding: "6px",
                          fontSize: "14px",
                          border: "1px solid #ddd",
                          borderRadius: "6px",
                          marginRight: "8px",
                        }}
                      />
                      <span style={{ fontSize: "13px", color: "#666" }}>
                        % ($
                        {(
                          (selectedBooking.servicePriceAtBooking *
                            partialRefundPercent) /
                          100
                        ).toFixed(2)}
                        )
                      </span>
                    </div>
                  )}
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      value="none"
                      checked={refundType === "none"}
                      onChange={(e) => setRefundType(e.target.value)}
                      style={{ marginRight: "8px" }}
                    />
                    No Refund (Keep Deposit)
                  </label>
                </div>
              </div>

              {/* Store Credit Option - Only show if refund amount > 0 */}
              {refundType !== "none" && (
                <div
                  style={{
                    marginBottom: "16px",
                    padding: "16px",
                    backgroundColor: "#f0fdf4",
                    border: "1px solid #86efac",
                    borderRadius: "8px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      gap: "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={useCredit}
                      onChange={(e) => setUseCredit(e.target.checked)}
                      style={{
                        marginTop: "2px",
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#166534",
                          marginBottom: "4px",
                        }}
                      >
                        💰 Issue Store Credit Instead
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#15803d",
                          marginBottom: "8px",
                        }}
                      >
                        Give customer{" "}
                        {(() => {
                          let refundAmt = 0;
                          if (refundType === "full")
                            refundAmt = selectedBooking.servicePriceAtBooking;
                          else if (refundType === "partial")
                            refundAmt =
                              (selectedBooking.servicePriceAtBooking *
                                partialRefundPercent) /
                              100;
                          return `$${refundAmt.toFixed(2)} credit`;
                        })()}{" "}
                        instead of card refund
                      </div>
                      <div style={{ fontSize: "12px", color: "#166534" }}>
                        ✓ Expires in 90 days &nbsp;•&nbsp; ✓ Auto-applies to
                        next booking &nbsp;•&nbsp; ✓ Encourages rebooking
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Policy Reminder */}
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#fff3cd",
                  border: "1px solid #ffc107",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  fontSize: "12px",
                  color: "#856404",
                }}
              >
                <strong>Cancellation Policy:</strong> Refunds are processed back
                to the original payment method within 5-10 business days.
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setSelectedBooking(null);
                  }}
                  disabled={processing}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    border: "none",
                    borderRadius: "8px",
                    cursor: processing ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCancelWithRefund}
                  disabled={processing || !cancelReason}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor:
                      processing || !cancelReason ? "#ccc" : "#F44336",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor:
                      processing || !cancelReason ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {processing ? "Processing..." : "Confirm Cancellation"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reassign Staff Modal */}
        {showReassignModal && reassignBooking && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "12px",
                maxWidth: "500px",
                width: "100%",
                maxHeight: "90vh",
                overflow: "auto",
              }}
            >
              <h2
                style={{
                  margin: "0 0 16px 0",
                  fontSize: "18px",
                  fontWeight: "600",
                  color: "#1a1a1a",
                }}
              >
                🔄 Reassign Staff for Booking
              </h2>

              {/* Booking Info */}
              <div
                style={{
                  padding: "12px",
                  backgroundColor: "#f9fafb",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  fontSize: "13px",
                  color: "#666",
                }}
              >
                <p style={{ margin: "4px 0" }}>
                  <strong>Service:</strong>{" "}
                  {reassignBooking.serviceId?.name || "N/A"}
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Customer:</strong>{" "}
                  {reassignBooking.customerId
                    ? `${reassignBooking.customerId.firstName} ${reassignBooking.customerId.lastName}`
                    : reassignBooking.customerName || "Walk-in"}
                </p>
                <p style={{ margin: "4px 0" }}>
                  <strong>Date:</strong>{" "}
                  {dayjs(reassignBooking.appointmentDate).format("MMM D, YYYY")}{" "}
                  at {reassignBooking.startTime}
                </p>
                {reassignBooking.importedStaffName && (
                  <p style={{ margin: "8px 0 4px", color: "#ef4444" }}>
                    <strong>Original CSV Staff Name:</strong>{" "}
                    {reassignBooking.importedStaffName}
                  </p>
                )}
                <p style={{ margin: "4px 0" }}>
                  <strong>Currently Assigned:</strong>{" "}
                  {reassignBooking.staffId?.userId?.firstName}{" "}
                  {reassignBooking.staffId?.userId?.lastName}
                </p>
              </div>

              {/* Staff Selection */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "8px",
                  }}
                >
                  Select Professional *
                </label>
                <select
                  value={selectedStaffId}
                  onChange={(e) => setSelectedStaffId(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "14px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    backgroundColor: "white",
                  }}
                >
                  <option value="">-- Select a professional --</option>
                  {availableStaff.map((staff) => (
                    <option key={staff._id} value={staff._id}>
                      {staff.name ||
                        `${staff.userId?.firstName || ""} ${
                          staff.userId?.lastName || ""
                        }`.trim()}
                    </option>
                  ))}
                </select>
                {availableStaff.length === 0 && (
                  <p
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      marginTop: "8px",
                    }}
                  >
                    No active professionals found at this location.
                  </p>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: "12px" }}>
                <button
                  onClick={() => {
                    setShowReassignModal(false);
                    setReassignBooking(null);
                    setSelectedStaffId("");
                  }}
                  disabled={reassigning}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor: "#f5f5f5",
                    color: "#666",
                    border: "none",
                    borderRadius: "8px",
                    cursor: reassigning ? "not-allowed" : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReassignStaff}
                  disabled={reassigning || !selectedStaffId}
                  style={{
                    flex: 1,
                    padding: "12px",
                    fontSize: "14px",
                    fontWeight: "500",
                    backgroundColor:
                      reassigning || !selectedStaffId ? "#ccc" : "#0ea5e9",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor:
                      reassigning || !selectedStaffId
                        ? "not-allowed"
                        : "pointer",
                    fontFamily: "Inter, sans-serif",
                  }}
                >
                  {reassigning ? "Reassigning..." : "Reassign Staff"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default Bookings;
